/**
 * wizardSteps — Phase G4A CP2.
 *
 * Ten step components in one file (per CP2 dispatch — keep the
 * component surface tight; the wizard shell is the orchestrator).
 * Each step receives:
 *   - `data`: the current slice of `wizard_data[step.key]`
 *   - `onChange(newSlice)`: shallow-merge callback
 *   - `readOnly`: renders inputs as read-only (used on Review step's
 *     inline-edit affordances if we ever wire them; currently no
 *     step passes true — reserved for future)
 *
 * Steps 9 (Generate) and 10 (Quality Score) do NOT persist
 * `wizard_data` — they render the honest-gate copy approved during
 * CP1 audit ("AI generation lands in the next Grant Writer update").
 *
 * Voice discipline: field labels + placeholders are neutral and
 * factual. Explicitly do NOT use:
 *   - "guaranteed to win", "will get funded", "will receive",
 *     "definitely qualify", "sure to be awarded" (G4-specific)
 *   - the G2A baseline set (guaranteed, coming soon, beta, …)
 */
import { Sparkles, Gauge, Plus, Trash2 } from "lucide-react";
import AIDisclosureBadge from "@/components/AIDisclosureBadge";
import DocumentVaultPicker from "./DocumentVaultPicker";
import GenerateProposalStep from "./GenerateProposalStep";
import QualityScoreStep from "./QualityScoreStep";
import { DOC_ATTACH_HINTS, HONEST_GATE_COPY } from "./utils";


// ---------------------------------------------------------------------
// Reusable field primitives — kept inline (no separate file needed;
// they're small).
// ---------------------------------------------------------------------

function LabeledInput({ label, testId, value, onChange, type = "text", placeholder, hint }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-v2-caption uppercase tracking-[0.14em] text-v2-textMuted">
        {label}
      </span>
      <input
        data-testid={testId}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-xl border border-v2-border bg-v2-background px-3 py-2 text-v2-body-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent/40"
      />
      {hint && <span className="text-v2-caption text-v2-textMuted">{hint}</span>}
    </label>
  );
}

function LabeledNumber({ label, testId, value, onChange, placeholder, hint }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-v2-caption uppercase tracking-[0.14em] text-v2-textMuted">
        {label}
      </span>
      <input
        data-testid={testId}
        type="number"
        value={value == null ? "" : value}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? null : Number(raw));
        }}
        placeholder={placeholder}
        className="rounded-xl border border-v2-border bg-v2-background px-3 py-2 text-v2-body-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent/40"
      />
      {hint && <span className="text-v2-caption text-v2-textMuted">{hint}</span>}
    </label>
  );
}

function LabeledTextarea({ label, testId, value, onChange, placeholder, rows = 4, hint }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-v2-caption uppercase tracking-[0.14em] text-v2-textMuted">
        {label}
      </span>
      <textarea
        data-testid={testId}
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-xl border border-v2-border bg-v2-background px-3 py-2 text-v2-body-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent/40 resize-none"
      />
      {hint && <span className="text-v2-caption text-v2-textMuted">{hint}</span>}
    </label>
  );
}

function LabeledCheckbox({ label, testId, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input
        data-testid={testId}
        type="checkbox"
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-v2-border text-v2-accent focus:ring-2 focus:ring-v2-accent/40"
      />
      <span className="text-v2-body-sm text-v2-text">{label}</span>
    </label>
  );
}


// ---------------------------------------------------------------------
// Step 1 — Business Overview
// ---------------------------------------------------------------------

export function BusinessOverviewStep({ data, onChange }) {
  const d = data || {};
  const hint = DOC_ATTACH_HINTS.business_overview;
  return (
    <div className="space-y-6" data-testid="wizard-step-business_overview">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LabeledInput
          label="Legal business name"
          testId="wizard-field-business_overview-name"
          value={d.name}
          onChange={(v) => onChange({ ...d, name: v })}
          placeholder="Acme Community Services LLC"
        />
        <LabeledInput
          label="EIN"
          testId="wizard-field-business_overview-ein"
          value={d.ein}
          onChange={(v) => onChange({ ...d, ein: v })}
          placeholder="12-3456789"
        />
        <LabeledInput
          label="Entity type"
          testId="wizard-field-business_overview-entity_type"
          value={d.entity_type}
          onChange={(v) => onChange({ ...d, entity_type: v })}
          placeholder="LLC / C-Corp / Nonprofit / …"
        />
        <LabeledInput
          label="State of formation"
          testId="wizard-field-business_overview-state"
          value={d.state}
          onChange={(v) => onChange({ ...d, state: v })}
          placeholder="CA"
        />
        <LabeledNumber
          label="Year founded"
          testId="wizard-field-business_overview-founded_year"
          value={d.founded_year}
          onChange={(v) => onChange({ ...d, founded_year: v })}
          placeholder="2020"
        />
      </div>
      <LabeledTextarea
        label="Mission statement"
        testId="wizard-field-business_overview-mission"
        value={d.mission}
        onChange={(v) => onChange({ ...d, mission: v })}
        placeholder="Describe the mission your organisation exists to advance."
        rows={3}
      />
      <LabeledTextarea
        label="Short description"
        testId="wizard-field-business_overview-description"
        value={d.description}
        onChange={(v) => onChange({ ...d, description: v })}
        placeholder="A 2-3 sentence orientation for a reviewer meeting your organisation for the first time."
        rows={3}
      />
      <DocumentVaultPicker
        docType={hint.docType}
        label={hint.label}
        value={d[hint.wizardField]}
        onChange={(id) => onChange({ ...d, [hint.wizardField]: id })}
      />
    </div>
  );
}


// ---------------------------------------------------------------------
// Step 2 — Funding Request
// ---------------------------------------------------------------------

export function FundingRequestStep({ data, onChange }) {
  const d = data || {};
  return (
    <div className="space-y-6" data-testid="wizard-step-funding_request">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LabeledNumber
          label="Amount requested (USD)"
          testId="wizard-field-funding_request-amount"
          value={d.amount_requested}
          onChange={(v) => onChange({ ...d, amount_requested: v })}
          placeholder="50000"
        />
        <LabeledNumber
          label="Matching funds available (USD)"
          testId="wizard-field-funding_request-match_amount"
          value={d.match_amount}
          onChange={(v) => onChange({ ...d, match_amount: v })}
          placeholder="10000"
          hint="Leave blank if no match funding is available."
        />
      </div>
      <LabeledCheckbox
        label="Matching funds are available for this project"
        testId="wizard-field-funding_request-match_available"
        checked={d.match_funding_available}
        onChange={(v) => onChange({ ...d, match_funding_available: v })}
      />
      <LabeledTextarea
        label="Use of funds"
        testId="wizard-field-funding_request-use_of_funds"
        value={d.use_of_funds}
        onChange={(v) => onChange({ ...d, use_of_funds: v })}
        placeholder="Describe how the requested funds will be spent — categories, milestones, or line items."
        rows={5}
      />
    </div>
  );
}


// ---------------------------------------------------------------------
// Step 3 — Problem Statement
// ---------------------------------------------------------------------

export function ProblemStatementStep({ data, onChange }) {
  const d = data || {};
  return (
    <div className="space-y-6" data-testid="wizard-step-problem_statement">
      <LabeledTextarea
        label="Problem narrative"
        testId="wizard-field-problem_statement-narrative"
        value={d.problem_narrative}
        onChange={(v) => onChange({ ...d, problem_narrative: v })}
        placeholder="Describe the problem your project addresses. Include scale, urgency, and evidence."
        rows={6}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LabeledInput
          label="Target population"
          testId="wizard-field-problem_statement-target_population"
          value={d.target_population}
          onChange={(v) => onChange({ ...d, target_population: v })}
          placeholder="e.g., low-income households in Fresno County"
        />
        <LabeledInput
          label="Data source (optional)"
          testId="wizard-field-problem_statement-data_source"
          value={d.data_source}
          onChange={(v) => onChange({ ...d, data_source: v })}
          placeholder="ACS 2023 estimates, county public-health report, …"
        />
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------
// Step 4 — Solution
// ---------------------------------------------------------------------

export function SolutionStep({ data, onChange }) {
  const d = data || {};
  const hint = DOC_ATTACH_HINTS.solution;
  return (
    <div className="space-y-6" data-testid="wizard-step-solution">
      <LabeledTextarea
        label="Approach narrative"
        testId="wizard-field-solution-approach"
        value={d.approach_narrative}
        onChange={(v) => onChange({ ...d, approach_narrative: v })}
        placeholder="Describe your approach — the intervention, the theory of change, and why it fits the problem."
        rows={6}
      />
      <LabeledTextarea
        label="Innovation (optional)"
        testId="wizard-field-solution-innovation"
        value={d.innovation_narrative}
        onChange={(v) => onChange({ ...d, innovation_narrative: v })}
        placeholder="If your approach uses a new method, tool, or partnership model, describe it here."
        rows={4}
      />
      <DocumentVaultPicker
        docType={hint.docType}
        label={hint.label}
        value={d[hint.wizardField]}
        onChange={(id) => onChange({ ...d, [hint.wizardField]: id })}
      />
    </div>
  );
}


// ---------------------------------------------------------------------
// Step 5 — Community Impact
// ---------------------------------------------------------------------

export function CommunityImpactStep({ data, onChange }) {
  const d = data || {};
  const hint = DOC_ATTACH_HINTS.community_impact;
  return (
    <div className="space-y-6" data-testid="wizard-step-community_impact">
      <LabeledTextarea
        label="Impact narrative"
        testId="wizard-field-community_impact-narrative"
        value={d.impact_narrative}
        onChange={(v) => onChange({ ...d, impact_narrative: v })}
        placeholder="Describe the community outcomes you expect. Reference target beneficiaries and any measurable indicators."
        rows={6}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LabeledNumber
          label="Beneficiaries served (estimate)"
          testId="wizard-field-community_impact-beneficiaries_count"
          value={d.beneficiaries_count}
          onChange={(v) => onChange({ ...d, beneficiaries_count: v })}
          placeholder="200"
        />
        <LabeledInput
          label="Measurement approach"
          testId="wizard-field-community_impact-measurement_approach"
          value={d.measurement_approach}
          onChange={(v) => onChange({ ...d, measurement_approach: v })}
          placeholder="Pre/post surveys, service-usage logs, third-party evaluation, …"
        />
      </div>
      <DocumentVaultPicker
        docType={hint.docType}
        label={hint.label}
        value={d[hint.wizardField]}
        onChange={(id) => onChange({ ...d, [hint.wizardField]: id })}
      />
    </div>
  );
}


// ---------------------------------------------------------------------
// Step 6 — Budget
// ---------------------------------------------------------------------

export function BudgetStep({ data, onChange }) {
  const d = data || {};
  const lineItems = Array.isArray(d.line_items) ? d.line_items : [];
  const hint = DOC_ATTACH_HINTS.budget;

  function updateItem(idx, patch) {
    const next = lineItems.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange({ ...d, line_items: next });
  }
  function addItem() {
    onChange({
      ...d,
      line_items: [...lineItems, { category: "", amount: 0, description: "" }],
    });
  }
  function removeItem(idx) {
    onChange({ ...d, line_items: lineItems.filter((_, i) => i !== idx) });
  }

  const total = lineItems.reduce((sum, it) => sum + Number(it.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="wizard-step-budget">
      <div className="flex flex-col gap-3">
        {lineItems.length === 0 && (
          <p className="text-v2-body-sm text-v2-textMuted">
            No budget line items yet. Add rows for each category of spend.
          </p>
        )}
        {lineItems.map((it, idx) => (
          <div
            key={idx}
            data-testid={`wizard-field-budget-line-${idx}`}
            className="grid grid-cols-12 gap-3 rounded-2xl border border-v2-border bg-v2-surfaceSubtle/40 p-3 items-start"
          >
            <div className="col-span-12 md:col-span-4">
              <LabeledInput
                label="Category"
                testId={`wizard-field-budget-line-${idx}-category`}
                value={it.category}
                onChange={(v) => updateItem(idx, { category: v })}
                placeholder="Salaries / Equipment / Travel / …"
              />
            </div>
            <div className="col-span-8 md:col-span-3">
              <LabeledNumber
                label="Amount (USD)"
                testId={`wizard-field-budget-line-${idx}-amount`}
                value={it.amount}
                onChange={(v) => updateItem(idx, { amount: v })}
                placeholder="12000"
              />
            </div>
            <div className="col-span-12 md:col-span-4">
              <LabeledInput
                label="Description"
                testId={`wizard-field-budget-line-${idx}-description`}
                value={it.description}
                onChange={(v) => updateItem(idx, { description: v })}
                placeholder="What this covers"
              />
            </div>
            <div className="col-span-4 md:col-span-1 flex items-end justify-end pb-1">
              <button
                type="button"
                data-testid={`wizard-field-budget-line-${idx}-remove`}
                onClick={() => removeItem(idx)}
                className="text-v2-textMuted hover:text-red-400 p-2 rounded-full hover:bg-red-500/10 transition-colors"
                aria-label="Remove line item"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          data-testid="wizard-field-budget-add-line"
          onClick={addItem}
          className="inline-flex items-center gap-1.5 rounded-full self-start px-3.5 py-1.5 text-v2-body-sm text-v2-text bg-v2-surfaceSubtle hover:bg-v2-surface border border-v2-border transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add line item
        </button>
        {lineItems.length > 0 && (
          <p className="text-v2-body-sm text-v2-textMuted mt-1">
            Total: <span className="text-v2-text font-semibold">${total.toLocaleString()}</span>
          </p>
        )}
      </div>
      <DocumentVaultPicker
        docType={hint.docType}
        label={hint.label}
        value={d[hint.wizardField]}
        onChange={(id) => onChange({ ...d, [hint.wizardField]: id })}
      />
    </div>
  );
}


// ---------------------------------------------------------------------
// Step 7 — Timeline
// ---------------------------------------------------------------------

export function TimelineStep({ data, onChange }) {
  const d = data || {};
  const milestones = Array.isArray(d.milestones) ? d.milestones : [];

  function updateItem(idx, patch) {
    const next = milestones.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    onChange({ ...d, milestones: next });
  }
  function addItem() {
    onChange({
      ...d,
      milestones: [...milestones, { phase: "", target_date: "", deliverables: "" }],
    });
  }
  function removeItem(idx) {
    onChange({ ...d, milestones: milestones.filter((_, i) => i !== idx) });
  }

  return (
    <div className="space-y-6" data-testid="wizard-step-timeline">
      {milestones.length === 0 && (
        <p className="text-v2-body-sm text-v2-textMuted">
          No milestones defined yet. Add rows for each project phase or key delivery moment.
        </p>
      )}
      {milestones.map((m, idx) => (
        <div
          key={idx}
          data-testid={`wizard-field-timeline-milestone-${idx}`}
          className="grid grid-cols-12 gap-3 rounded-2xl border border-v2-border bg-v2-surfaceSubtle/40 p-3 items-start"
        >
          <div className="col-span-12 md:col-span-4">
            <LabeledInput
              label="Phase / milestone"
              testId={`wizard-field-timeline-milestone-${idx}-phase`}
              value={m.phase}
              onChange={(v) => updateItem(idx, { phase: v })}
              placeholder="Design phase / Pilot launch / …"
            />
          </div>
          <div className="col-span-6 md:col-span-3">
            <LabeledInput
              label="Target date"
              testId={`wizard-field-timeline-milestone-${idx}-target_date`}
              type="date"
              value={m.target_date}
              onChange={(v) => updateItem(idx, { target_date: v })}
            />
          </div>
          <div className="col-span-12 md:col-span-4">
            <LabeledInput
              label="Deliverables"
              testId={`wizard-field-timeline-milestone-${idx}-deliverables`}
              value={m.deliverables}
              onChange={(v) => updateItem(idx, { deliverables: v })}
              placeholder="Signed vendor contracts, pilot report, …"
            />
          </div>
          <div className="col-span-6 md:col-span-1 flex items-end justify-end pb-1">
            <button
              type="button"
              data-testid={`wizard-field-timeline-milestone-${idx}-remove`}
              onClick={() => removeItem(idx)}
              className="text-v2-textMuted hover:text-red-400 p-2 rounded-full hover:bg-red-500/10 transition-colors"
              aria-label="Remove milestone"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        data-testid="wizard-field-timeline-add-milestone"
        onClick={addItem}
        className="inline-flex items-center gap-1.5 rounded-full self-start px-3.5 py-1.5 text-v2-body-sm text-v2-text bg-v2-surfaceSubtle hover:bg-v2-surface border border-v2-border transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Add milestone
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------
// Step 8 — Review (summary of steps 1-7 + free-form notes)
// ---------------------------------------------------------------------

function ReviewSection({ heading, children, empty = false }) {
  return (
    <div className="rounded-2xl border border-v2-border bg-v2-surfaceSubtle/40 p-4">
      <h3 className="text-v2-body-sm font-semibold text-v2-text mb-2">{heading}</h3>
      {empty ? (
        <p className="text-v2-caption text-v2-textMuted italic">Not yet provided.</p>
      ) : (
        <div className="text-v2-body-sm text-v2-textMuted space-y-1">{children}</div>
      )}
    </div>
  );
}

export function ReviewStep({ data, wizardDataFull, onChange }) {
  const d = data || {};
  const wd = wizardDataFull || {};
  const bo = wd.business_overview || {};
  const fr = wd.funding_request || {};
  const ps = wd.problem_statement || {};
  const sol = wd.solution || {};
  const ci = wd.community_impact || {};
  const bu = wd.budget || {};
  const tl = wd.timeline || {};
  const totalBudget = Array.isArray(bu.line_items)
    ? bu.line_items.reduce((s, it) => s + Number(it.amount || 0), 0)
    : 0;

  return (
    <div className="space-y-4" data-testid="wizard-step-review">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ReviewSection heading="Business Overview" empty={!bo.name}>
          <p><strong>{bo.name}</strong></p>
          {bo.entity_type && <p>Entity: {bo.entity_type}</p>}
          {bo.state && <p>State: {bo.state}</p>}
          {bo.mission && <p className="mt-1">{bo.mission}</p>}
        </ReviewSection>
        <ReviewSection heading="Funding Request" empty={!fr.amount_requested}>
          <p>Amount requested: <strong>${Number(fr.amount_requested || 0).toLocaleString()}</strong></p>
          {fr.match_funding_available && (
            <p>Match funds available: ${Number(fr.match_amount || 0).toLocaleString()}</p>
          )}
          {fr.use_of_funds && <p className="mt-1 line-clamp-3">{fr.use_of_funds}</p>}
        </ReviewSection>
        <ReviewSection heading="Problem Statement" empty={!ps.problem_narrative}>
          <p className="line-clamp-4">{ps.problem_narrative}</p>
          {ps.target_population && (
            <p className="mt-1">Target: {ps.target_population}</p>
          )}
        </ReviewSection>
        <ReviewSection heading="Solution" empty={!sol.approach_narrative}>
          <p className="line-clamp-4">{sol.approach_narrative}</p>
        </ReviewSection>
        <ReviewSection heading="Community Impact" empty={!ci.impact_narrative}>
          <p className="line-clamp-4">{ci.impact_narrative}</p>
          {ci.beneficiaries_count && (
            <p className="mt-1">Beneficiaries: {ci.beneficiaries_count}</p>
          )}
        </ReviewSection>
        <ReviewSection heading="Budget" empty={!Array.isArray(bu.line_items) || bu.line_items.length === 0}>
          <p>Line items: {(bu.line_items || []).length}</p>
          <p>Total: <strong>${totalBudget.toLocaleString()}</strong></p>
        </ReviewSection>
        <ReviewSection heading="Timeline" empty={!Array.isArray(tl.milestones) || tl.milestones.length === 0}>
          <p>Milestones: {(tl.milestones || []).length}</p>
        </ReviewSection>
      </div>
      <LabeledTextarea
        label="Your notes (personal scratch pad)"
        testId="wizard-field-review-user_notes"
        value={d.user_notes}
        onChange={(v) => onChange({ ...d, user_notes: v })}
        placeholder="Anything to remember before generation? These notes stay with you — they are not sent to the AI Grant Writer."
        rows={4}
      />
    </div>
  );
}


// ---------------------------------------------------------------------
// Step 9 — Generate Proposal
//
// G4A CP2 shipped this as a passive honest-gate placeholder. G4B CP2
// lights it up: the surface delegates to `GenerateProposalStep` which
// fetches the rolling-30d budget, renders the Generate CTA, handles
// the sequential 30-55s LLM run, and navigates to /proposals/{id}/draft
// on success.
//
// The passive placeholder is retained (below, `LegacyGenerateStep`) for
// audit-trail readability of the CP2 diff — not exported.
// ---------------------------------------------------------------------

export function GenerateStep({ proposalId }) {
  return <GenerateProposalStep proposalId={proposalId} />;
}


function LegacyGenerateStep() {  // eslint-disable-line no-unused-vars
  // Kept in the file (not exported) as a reference for the G4A CP2
  // shape. Delete after G4B CP2 approval if a cleaner diff is desired.
  return (
    <div className="space-y-6" data-testid="wizard-step-generate-legacy">
      <div className="rounded-3xl border border-v2-border bg-v2-surface p-8 flex flex-col items-center gap-4 text-center">
        <div
          className="rounded-full p-4"
          style={{
            background: "rgba(167,139,250,0.14)",
            border: "1px solid rgba(167,139,250,0.42)",
          }}
        >
          <Sparkles className="w-6 h-6 text-v2-accent" />
        </div>
        <div className="max-w-md">
          <h3 className="text-v2-heading-sm font-semibold text-v2-text">
            Generate AI Draft
          </h3>
          <p className="mt-2 text-v2-body-sm text-v2-textMuted">
            {HONEST_GATE_COPY}
          </p>
        </div>
        <AIDisclosureBadge inline />
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------
// Step 10 — Quality Score (delegated to `QualityScoreStep` — Phase G4C CP2)
// ---------------------------------------------------------------------
// The placeholder implementation from G4A was replaced when G4C CP2
// activated the wizard step 10 surface. `QualityScoreStep` handles
// the entire happy path (overall + subscores + weak-section
// highlights + recommendations) AND every non-happy path (empty,
// loading, error, feature-disabled, Premium upsell, kill-switch)
// inline — no toasts. The import at the top of this file exposes
// it to `renderStep` below; consumers that need `QualityScoreStep`
// as a named export from `wizardSteps` should import directly from
// `./QualityScoreStep`.


// ---------------------------------------------------------------------
// Renderer — resolves a step number to the right component.
// ---------------------------------------------------------------------

export function renderStep({ stepNumber, stepData, wizardDataFull, onChange, proposalId }) {
  switch (stepNumber) {
    case 1:
      return <BusinessOverviewStep data={stepData} onChange={onChange} />;
    case 2:
      return <FundingRequestStep data={stepData} onChange={onChange} />;
    case 3:
      return <ProblemStatementStep data={stepData} onChange={onChange} />;
    case 4:
      return <SolutionStep data={stepData} onChange={onChange} />;
    case 5:
      return <CommunityImpactStep data={stepData} onChange={onChange} />;
    case 6:
      return <BudgetStep data={stepData} onChange={onChange} />;
    case 7:
      return <TimelineStep data={stepData} onChange={onChange} />;
    case 8:
      return (
        <ReviewStep
          data={stepData}
          wizardDataFull={wizardDataFull}
          onChange={onChange}
        />
      );
    case 9:
      return <GenerateStep proposalId={proposalId} />;
    case 10:
      return <QualityScoreStep proposalId={proposalId} />;
    default:
      return null;
  }
}
